import { Link, useLocation } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { Seo } from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const CONTENT = {
  shipping: {
    title: "Shipping & Delivery",
    intro: "Fast, tracked delivery across Ireland and the European Union from our Irish base.",
    sections: [
      { h: "Ireland", p: "Orders within the Republic of Ireland and Northern Ireland are typically delivered within 1–3 working days via tracked courier. Free shipping on Irish orders over €100 (excludes feed, bedding & certain overweight items — full details in our Terms & Conditions)." },
      { h: "European Union", p: "We ship to all EU member states. Delivery usually takes 3–6 working days depending on destination. Duties and taxes are calculated at checkout where applicable." },
      { h: "Order tracking", p: "You'll receive a tracking link by email as soon as your order is dispatched." },
    ],
  },
  returns: {
    title: "Returns & Exchanges",
    intro: "Not quite right? Our easy 14-day returns make it simple.",
    sections: [
      { h: "14-day returns", p: "Return unused items in their original packaging within 14 days of delivery for a refund or exchange." },
      { h: "How to return", p: "Contact us at info@boomerang.ie with your order number and we'll guide you through the process." },
      { h: "Refunds", p: "Refunds are processed to your original payment method within 5–7 working days of receiving your return." },
    ],
  },
  "size-guide": {
    title: "Size Guide",
    intro: "Find the perfect fit for rugs, boots and bridles.",
    sections: [
      { h: "Horse rugs", p: "Measure from the centre of the chest to the point of the buttock (in cm). Kentucky rugs use the continental sizing system, for example 125cm, 130cm, 135cm." },
      { h: "Boots & leg protection", p: "Sizing typically runs Pony / Cob / Full. Refer to each product page for brand-specific guidance." },
      { h: "Bridles (Dy'on)", p: "Dy'on bridles are available in Pony, Cob and Full sizes with adjustable fittings for a precise fit." },
    ],
  },
  "care-guide": {
    title: "Care Guide",
    intro: "Keep your equestrian equipment performing season after season.",
    sections: [
      { h: "Rugs", p: "Most Kentucky rugs are machine washable at 30°C. Use a technical wash, avoid fabric softener, and re-proof waterproof rugs as needed." },
      { h: "Leatherwork", p: "Clean leather with a damp cloth and saddle soap, then condition regularly to prevent drying and cracking." },
      { h: "Grooming tools", p: "Rinse and dry brushes after use. Grooming Deluxe tools are built for daily professional use." },
    ],
  },
  about: {
    title: "About Boomerang.ie",
    intro: "Ireland's trusted home for premium equestrian brands.",
    sections: [
      { h: "Official reseller", p: "Boomerang.ie Horse & Country is an official Irish reseller of Kentucky Horsewear, Dy'on, Velari and Grooming Deluxe, offering authentic products with full manufacturer warranty." },
      { h: "Our mission", p: "To bring riders, grooms and yards across Ireland and the EU the very best in equestrian equipment, backed by expert advice and fast, reliable shipping." },
    ],
  },
  contact: {
    title: "Contact Us",
    intro: "We're here to help with product advice, orders and returns.",
    sections: [
      { h: "Email", p: "info@boomerang.ie, and we aim to reply within one working day." },
      { h: "Support hours", p: "Monday to Friday, 9:00–17:30 (Irish time)." },
    ],
  },
};

export default function StaticPage({ slug }) {
  const location = useLocation();
  const { tx } = useI18n();
  const key = slug || location.pathname.replace("/", "");
  const data = CONTENT[key] || CONTENT.about;
  return (
    <>
      <Seo title={data.title} description={data.intro} path={`/${key}`} />
      <div className="border-b border-border bg-[hsl(var(--surface-2))]">
        <div className="container-site py-12 lg:py-16">
          <h1 className="font-display text-4xl sm:text-5xl">{tx(data.title)}</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">{tx(data.intro)}</p>
        </div>
      </div>
      <div className="container-site grid gap-10 py-12 lg:grid-cols-3 lg:py-16">
        <div className="prose prose-neutral max-w-none lg:col-span-2 prose-headings:font-display">
          {data.sections.map((s, i) => (<div key={i}><h2>{tx(s.h)}</h2><p>{tx(s.p)}</p></div>))}
        </div>
        <aside className="h-fit rounded-xl border border-border bg-secondary/40 p-6">
          <h3 className="font-display text-lg">{tx("Need a hand?")}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{tx("Our equestrian specialists are happy to help you choose.")}</p>
          <Button asChild className="mt-4 w-full"><LangLink to="/brand/kentucky">{tx("Shop Kentucky")}</LangLink></Button>
          <div className="mt-4 text-sm">
            <LangLink to="/shipping" className="block py-1 text-muted-foreground hover:text-foreground">{tx("Shipping & Delivery")}</LangLink>
            <LangLink to="/returns" className="block py-1 text-muted-foreground hover:text-foreground">{tx("Returns")}</LangLink>
            <LangLink to="/size-guide" className="block py-1 text-muted-foreground hover:text-foreground">{tx("Size Guide")}</LangLink>
            <LangLink to="/care-guide" className="block py-1 text-muted-foreground hover:text-foreground">{tx("Care Guide")}</LangLink>
          </div>
        </aside>
      </div>
    </>
  );
}
